import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { AppRuntimeConfig } from '../../config/models/app-runtime-config.model';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  apply(config: AppRuntimeConfig): void {
    const root = this.document.documentElement;

    const tokens: Record<string, string> = {
      '--brand-color-primary':
        config.theme.colors.primary,

      '--brand-color-on-primary':
        config.theme.colors.onPrimary,

      '--brand-color-secondary':
        config.theme.colors.secondary,

      '--brand-color-on-secondary':
        config.theme.colors.onSecondary,

      '--brand-color-tertiary':
        config.theme.colors.tertiary,

      '--brand-color-on-tertiary':
        config.theme.colors.onTertiary,

      '--brand-color-background':
        config.theme.colors.background,

      '--brand-color-surface':
        config.theme.colors.surface,

      '--brand-color-text':
        config.theme.colors.text,

      '--brand-color-muted-text':
        config.theme.colors.mutedText,

      '--brand-color-border':
        config.theme.colors.border,

      '--brand-color-success':
        config.theme.colors.success,

      '--brand-color-warning':
        config.theme.colors.warning,

      '--brand-color-danger':
        config.theme.colors.danger,

      '--brand-color-label-blue':
        config.theme.colors.labelBlue,

      '--brand-color-label-orange':
        config.theme.colors.labelOrange,

      '--brand-color-market-place-background':
        config.theme.colors.marketPlaceBackground,

      '--brand-font-family':
        config.theme.typography.fontFamily,

      '--brand-heading-font-family':
        config.theme.typography.headingFontFamily,

      '--brand-base-font-size':
        config.theme.typography.baseFontSize,

      '--brand-heading-font-weight':
        String(
          config.theme.typography.headingFontWeight
        ),

      '--brand-radius-small':
        config.theme.radius.small,

      '--brand-radius-medium':
        config.theme.radius.medium,

      '--brand-radius-large':
        config.theme.radius.large,

      '--brand-radius-pill':
        config.theme.radius.pill,

      '--brand-spacing-small':
        config.theme.spacing.small,

      '--brand-spacing-medium':
        config.theme.spacing.medium,

      '--brand-spacing-large':
        config.theme.spacing.large
    };

    for (const [property, value] of Object.entries(tokens)) {
      root.style.setProperty(property, value);
    }

    root.lang = config.application.language;
    root.dataset['tenant'] = config.tenant;

    this.document.title =
      config.application.title;

    this.applyFavicon(
      config.assets.favicon
    );

    this.applyFontStylesheet(
      config.theme.typography.stylesheetUrl
    );
  }

  private applyFavicon(url: string): void {
    let favicon =
      this.document.querySelector<HTMLLinkElement>(
        'link[rel="icon"]'
      );

    if (!favicon) {
      favicon =
        this.document.createElement('link');

      favicon.rel = 'icon';

      this.document.head.appendChild(
        favicon
      );
    }

    favicon.href = url;
  }

  private applyFontStylesheet(
    url: string | undefined
  ): void {
    const existing =
      this.document.querySelector<HTMLLinkElement>(
        'link[data-brand-font]'
      );

    if (!url) {
      existing?.remove();
      return;
    }

    const link =
      existing ??
      this.document.createElement('link');

    link.rel = 'stylesheet';
    link.href = url;
    link.dataset['brandFont'] = 'true';

    if (!existing) {
      this.document.head.appendChild(link);
    }
  }
}
